import { CompositeSegment } from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/Segment/CompositeSegment';
import { Clip, ClipMetadata } from '@goldcast/player-config/src/types/clip-types';
import { isClipInOriginalLanguage } from '@goldcast/player-config/src/utils/clipUtils';
import { Word } from '@goldcast/player-config/src/types/transcript-types';
import { fullTranscriptStore } from '@/stores/fullTranscriptStore';
import { TranscriptProcessor } from '@/libs/transcriptUtils';

const calculateExcludesAndDurationWithData = ({
  start,
  end,
  edits,
  deletes,
  merges
}: Pick<ClipMetadata, 'start' | 'end' | 'edits' | 'deletes' | 'merges'>) => {
  const originalFullTranscript = fullTranscriptStore.getSnapshot();
  // Excludes and duration should ignore translated captions
  const transcriptProcessorWithoutTranslatedCaptions = new TranscriptProcessor(start, end, originalFullTranscript);
  transcriptProcessorWithoutTranslatedCaptions.getFinalCaptions(edits ?? {}, deletes ?? {}, merges ?? []);
  const excludes = transcriptProcessorWithoutTranslatedCaptions.getExcludes(
    (deletes ?? {}) as unknown as Parameters<typeof transcriptProcessorWithoutTranslatedCaptions.getFinalCaptions>[1]
  );
  const duration = transcriptProcessorWithoutTranslatedCaptions.getDuration(deletes ?? {});
  return { excludes, duration };
};

export const computeCaptionsWithData = ({
  start,
  end,
  edits,
  deletes,
  merges,
  isTranslated,
  translatedCaptionsSource = []
}: Pick<ClipMetadata, 'start' | 'end' | 'edits' | 'deletes' | 'merges'> & {
  isTranslated: boolean;
  translatedCaptionsSource?: Word[];
}) => {
  const originalFullTranscript = fullTranscriptStore.getSnapshot();
  let translatedCaptions: Word[] | undefined = undefined;
  if (isTranslated) {
    translatedCaptions = translatedCaptionsSource ?? [];
  }

  const transcriptProcessor = new TranscriptProcessor(start, end, originalFullTranscript, translatedCaptions);
  const captions = transcriptProcessor.getFinalCaptions(edits ?? {}, deletes ?? {}, merges ?? []);

  const { excludes, duration } = calculateExcludesAndDurationWithData({ start, end, edits, merges, deletes });

  return {
    captions,
    excludes,
    duration
  };
};

export const computeCaptionsForClip = (clip: Clip) => {
  const { start, end, edits, deletes, merges, captions } = clip.asset_metadata;
  return computeCaptionsWithData({
    start,
    end,
    edits,
    deletes,
    merges,
    isTranslated: !isClipInOriginalLanguage(clip),
    translatedCaptionsSource: captions
  });
};

export const computeCaptionsForComposite = (compositeSegment: CompositeSegment, clip: Clip) => {
  if (!compositeSegment.segment_metadata) {
    return;
  }
  const { start, end, edits, deletes, merges, captions } = compositeSegment.segment_metadata;

  return computeCaptionsWithData({
    start,
    end,
    edits,
    deletes,
    merges,
    isTranslated: !isClipInOriginalLanguage(clip),
    translatedCaptionsSource: captions
  });
};
